import { push } from "connected-react-router";
import { setSubmitSucceeded, startSubmit, stopSubmit } from "redux-form";
import { eventChannel, Task } from "redux-saga";
import {
  call,
  cancel,
  cancelled,
  fork,
  put,
  select,
  take,
} from "redux-saga/effects";
import { ICompanyRepository } from "src/domain/repository/company_repository";
import { createCompanyAPI } from "src/infra/api/service/company_api";
import { NotificationCommon } from "../../config/view_const/notification";
import { AppError, ErrorType } from "../../domain/model/app_error";
import { AppNotificationType } from "../../domain/model/app_notification_message";
import { Company } from "../../domain/model/company";
import { User } from "../../domain/model/user";
import {
  IApplyClientRepository,
  TRegisterClientRequest,
} from "../../domain/repository/apply_client_repository";
import {
  ICustomerRepository,
  TCreateCustomerRequest,
} from "../../domain/repository/customer_repository";
import { IUserRepository } from "../../domain/repository/user_repository";
import {
  createAuthUseCase,
  IAuthUseCase,
} from "../../domain/usecase/auth_usecase";
import { createApiClient, IApiClient } from "../../infra/api/client";
import { entity } from "../../infra/api/rpc/api";
import { createApplyClientAPI } from "../../infra/api/service/apply_client_api";
import { createCustomerAPI } from "../../infra/api/service/customer_api";
import { createUserAPI } from "../../infra/api/service/user_api";
import {
  createAppNotificationMessageActionCreator,
  IAppNotificationMessageActionCreator,
} from "../action/app_notification_message_action";
import {
  AuthActionType,
  createAuthActionCreator,
  IAuthActionCreator,
  IRequestApplyClientAction,
  IRequestCreateCustomerAction,
  IRequestLoginAction,
  IRequestPasswordResetAction,
} from "../action/auth_action";
import { AuthStateType } from "../auth_state_type";
import { AddAppNotificationRequest } from "../request/app_notification_message_request";
import {
  CreateCustomerResponse,
  GetMeResponse,
  LoginResponse,
  ObserveAuthListenerResponse,
} from "../response/auth_response";
import { AppState } from "../store/app_state";
import { handleErrorForHandler } from "./handleErrorForHandler";

const apiClient: IApiClient = createApiClient();
const userRepository: IUserRepository = createUserAPI(apiClient);
const companyRepository: ICompanyRepository = createCompanyAPI(apiClient);
const customerRepository: ICustomerRepository = createCustomerAPI(apiClient);
const authUseCase: IAuthUseCase = createAuthUseCase(
  userRepository,
  customerRepository
);
const actionCreator: IAuthActionCreator = createAuthActionCreator();
const applyClientRepository: IApplyClientRepository =
  createApplyClientAPI(apiClient);
const actionCreatorForNotification: IAppNotificationMessageActionCreator =
  createAppNotificationMessageActionCreator();

const ApplyClientFormNames: string[] = [
  "ApplyClientRegisterForm",
  "ApplyClientDemoRegisterForm",
];

let authChannelTask: Task;

const authChannel = () => {
  const channel = eventChannel((emit) => {
    authUseCase.onAuthListener(
      (error: any, data?: { user: User; authState: AuthStateType }) => {
        emit({ error, data });
      }
    );

    const unsubscribe = () => {
      authUseCase.offAuthListener();
    };

    return unsubscribe;
  });
  return channel;
};

function* onAuthListener() {
  const channel = yield call(authChannel);
  while (true) {
    try {
      const { error, data } = yield take(channel);
      if (error) {
        throw error;
      }
      const selector = (state: AppState) => state.router;
      const _state: any = yield select(selector);
      const res: ObserveAuthListenerResponse =
        new ObserveAuthListenerResponse();
      res.user = data.user;

      if (data.user.client) {
        const company: Company = yield call(
          getCompany,
          data.user.client.companyID
        );
        res.company = company;
      }

      res.authState = data.authState;
      yield put(actionCreator.observeAuthListenerAction(true, res));
      if (
        _state.location.state &&
        _state.location.state.from.pathname !== "/logout"
      ) {
        yield put(push({ pathname: _state.location.state.from.pathname }));
      }
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(actionCreator.observeAuthListenerAction(false));
    } finally {
      if (yield cancelled()) {
        channel.close();
      }
    }
  }
}

function* handleRequestOnAuthListenerInAuth() {
  while (true) {
    try {
      yield take(AuthActionType.REQUEST_ON_AUTH_LISTENER);
      authChannelTask = yield fork(onAuthListener);
      yield put(actionCreator.callbackOnAuthListenerAction(true, {}));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(actionCreator.callbackOnAuthListenerAction(false));
    }
  }
}

function* handleRequestOffAuthListenerInAuth() {
  while (true) {
    try {
      yield take(AuthActionType.REQUEST_OFF_AUTH_LISTENER);
      yield cancel(authChannelTask);
      yield put(actionCreator.callbackOffAuthListenerAction(true, {}));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(actionCreator.callbackOffAuthListenerAction(false));
    }
  }
}

function* handleLoginInAuth() {
  while (true) {
    try {
      const action: IRequestLoginAction = yield take(
        AuthActionType.REQUEST_LOGIN
      );
      yield put(startSubmit("LoginForm"));
      yield call(login, action.item.email, action.item.password);
      const res: LoginResponse = new LoginResponse();
      yield put(actionCreator.callbackLoginAction(true, res));
      yield put(stopSubmit("LoginForm"));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(actionCreator.callbackLoginAction(false));
      yield put(stopSubmit("LoginForm", { _error: error.toString() }));
    }
  }
}

function* handleLogoutInAuth() {
  while (true) {
    try {
      yield take(AuthActionType.REQUEST_LOGOUT);
      yield call(logout);
      yield put(actionCreator.callbackLogoutAction(true));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(actionCreator.callbackLogoutAction(false));
    }
  }
}

function* handlePasswordResetInAuth() {
  while (true) {
    try {
      const action: IRequestPasswordResetAction = yield take(
        AuthActionType.REQUEST_PASSWORD_RESET
      );
      yield put(startSubmit("passwordResetForm"));
      yield call(passwordReset, action.item.email);
      yield put(actionCreator.callbackPasswordResetAction(true));
      yield put(stopSubmit("passwordResetForm"));
      yield put(setSubmitSucceeded("passwordResetForm"));

      const notificationReq: AddAppNotificationRequest =
        new AddAppNotificationRequest();
      notificationReq.type = AppNotificationType.INFO;
      notificationReq.message = NotificationCommon.successResetPassword;
      yield put(actionCreatorForNotification.requestAddAction(notificationReq));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(stopSubmit("passwordResetForm", { _error: error.toString() }));
    }
  }
}

function* handleCreateInAuth() {
  while (true) {
    const action: IRequestCreateCustomerAction = yield take(
      AuthActionType.REQUEST_CREATE_CUSTOMER
    );

    if (
      action.item.createCustomerRequest.type ===
      entity.Customer.Type.Type_Expert
    ) {
      if (
        action.item.createCustomerRequest.campaign ===
        entity.Customer.Campaign.Campaign_SuperEngineer
      ) {
        try {
          yield put(startSubmit("SuperEngineerCreateForm"));
          const user: User = yield call(
            createCustomer,
            action.item.createCustomerRequest,
            action.item.email,
            action.item.password
          );
          const res: CreateCustomerResponse = new CreateCustomerResponse();
          res.user = user;
          yield put(actionCreator.callbackCreateCustomerAction(true, res));

          yield put(push(`/customer/tutorial`));
        } catch (error) {
          yield fork(handleErrorForHandler, error);
          yield put(actionCreator.callbackCreateCustomerAction(false));
          yield put(
            stopSubmit("SuperEngineerCreateForm", { _error: error.toString() })
          );
        }
      } else {
        try {
          yield put(startSubmit("CustomerCreateForm"));
          const user: User = yield call(
            createCustomer,
            action.item.createCustomerRequest,
            action.item.email,
            action.item.password
          );
          const res: CreateCustomerResponse = new CreateCustomerResponse();
          res.user = user;
          yield put(actionCreator.callbackCreateCustomerAction(true, res));

          yield put(push(`/customer/tutorial`));
        } catch (error) {
          yield fork(handleErrorForHandler, error);
          yield put(actionCreator.callbackCreateCustomerAction(false));
          yield put(
            stopSubmit("CustomerCreateForm", { _error: error.toString() })
          );
        }
      }
    } else {
      try {
        yield put(startSubmit("ProCreateForm"));
        const user: User = yield call(
          createCustomer,
          action.item.createCustomerRequest,
          action.item.email,
          action.item.password
        );
        const res: CreateCustomerResponse = new CreateCustomerResponse();
        res.user = user;
        yield put(actionCreator.callbackCreateCustomerAction(true, res));

        yield put(push(`/customer/tutorial`));
      } catch (error) {
        yield fork(handleErrorForHandler, error);
        yield put(actionCreator.callbackCreateCustomerAction(false));
        yield put(stopSubmit("ProCreateForm", { _error: error.toString() }));
      }
    }
  }
}

function* handleApplyClientInAuth() {
  while (true) {
    try {
      const action: IRequestApplyClientAction = yield take(
        AuthActionType.REQUEST_APPLY_CLIENT
      );
      yield put(startSubmit("ApplyClientRegisterForm"));
      yield call(applyClient, action.item.registerClientRequest);
      yield put(actionCreator.callbackApplyClientAction(true));
      yield put(stopSubmit("ApplyClientRegisterForm"));
      yield put(setSubmitSucceeded("ApplyClientRegisterForm"));

      const notificationReq: AddAppNotificationRequest =
        new AddAppNotificationRequest();
      notificationReq.type = AppNotificationType.INFO;
      notificationReq.message = NotificationCommon.successApplyClient;
      yield put(actionCreatorForNotification.requestAddAction(notificationReq));

      if (action.item.registerClientRequest.isDemo) {
        yield call(
          login,
          action.item.registerClientRequest.email,
          action.item.registerClientRequest.demoPassword
        );
        yield put(push(`/`));
      } else {
        yield put(push(`/apply_client/thanks`));
      }
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(actionCreator.callbackApplyClientAction(false));

      const submitError: string =
        error instanceof AppError &&
        error.type === ErrorType.FAIL_APPLY_CLIENT_EMAIL_ALREADY_USED
          ? error.message
          : error.toString();

      for (const form of ApplyClientFormNames) {
        yield put(stopSubmit(form, { _error: submitError }));
      }
    }
  }
}

// メッセージの最新情報をとるためポーリングでユーザー情報を取得する
// getMe単体実行でLayoutのStoreを更新する
function* handleGetMeInLayout() {
  while (true) {
    try {
      yield take(AuthActionType.REQUEST_GET_ME);
      const user: User = yield call(getMe);

      const res: GetMeResponse = new GetMeResponse();
      res.user = user;

      if (user.client) {
        const company: Company = yield call(getCompany, user.client.companyID);
        res.company = company;
      }

      yield put(actionCreator.callbackGetMeAction(true, res));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(actionCreator.callbackGetMeAction(false));
    }
  }
}

const createCustomer = (
  createCustomerRequest: TCreateCustomerRequest,
  email: string,
  password: string
): Promise<User> => {
  return authUseCase.createCustomer(createCustomerRequest, email, password);
};

const login = (email: string, password: string): Promise<void> => {
  return authUseCase.login(email, password);
};

const logout = (): Promise<void> => {
  return authUseCase.logout();
};

const passwordReset = (email: string): Promise<void> => {
  return authUseCase.sendPasswordResetEmail(email);
};

const applyClient = (
  registerClientRequest: TRegisterClientRequest
): Promise<void> => {
  return applyClientRepository.register(registerClientRequest);
};

const getMe = (): Promise<User> => {
  return userRepository.getMe();
};

const getCompany = (id: string): Promise<Company> => {
  return companyRepository.get(id);
};

export {
  handleRequestOnAuthListenerInAuth,
  handleRequestOffAuthListenerInAuth,
  handleCreateInAuth,
  handleLoginInAuth,
  handlePasswordResetInAuth,
  handleLogoutInAuth,
  handleApplyClientInAuth,
  handleGetMeInLayout,
};



// WEBPACK FOOTER //
// ./src/presentation/handler/auth_handler.ts